import React from "react";
import ReactDOM from "react-dom/client";

export type ExposableComponentType = (props: {
  attributes: Record<string, string>;
  elementRef: HTMLElement;
}) => JSX.Element;

export function reactToWebComponent({
  ApplicationWrapper,
  Component,
  tagName,
  observableAttributes,
}: {
  Component: ExposableComponentType;
  tagName: string;
  ApplicationWrapper: ({ children }: React.PropsWithChildren) => JSX.Element;
  observableAttributes: string[];
}) {
  const ComponentWrapper = getWebComponentWrapper({
    Component,
    ApplicationWrapper,
    observableAttributes,
  });

  customElements.define(tagName, ComponentWrapper);
}

function getWebComponentWrapper({
  Component,
  ApplicationWrapper,
  observableAttributes,
}: {
  Component: ExposableComponentType;
  ApplicationWrapper: ({ children }: React.PropsWithChildren) => JSX.Element;
  observableAttributes: string[];
}) {
  return class ReactWebComponentWrapper extends HTMLElement {
    // The browser calls this method when the element is
    // added to the DOM.
    private root: ReactDOM.Root | null;
    static observedAttributes = observableAttributes;

    constructor() {
      super();
      this.root = ReactDOM.createRoot(this);
    }

    connectedCallback() {
      this.renderComponent();
    }

    disconnectedCallback() {
      if (this.root) {
        this.root.unmount();
      }
    }

    attributeChangedCallback() {
      this.renderComponent();
    }

    renderComponent() {
      const attributes = this.getAttributeNames();
      const attributesData = attributes.reduce<Record<string, string>>(
        (acc, curr) => {
          acc[curr] = this.getAttribute(curr) || "true";
          return acc;
        },
        {}
      );
      this?.root?.render(
        <ApplicationWrapper>
          <Component attributes={{ ...attributesData }} elementRef={this} />
        </ApplicationWrapper>
      );
    }
  };
}

export function registerWebComponents({
  componentList,
  ApplicationWrapper,
}: {
  componentList: ComponentListStructure;
  ApplicationWrapper: ({ children }: React.PropsWithChildren) => JSX.Element;
}) {
  Object.keys(componentList).forEach((tagName) => {
    const tagDetails = componentList[tagName];
    reactToWebComponent({
      Component: tagDetails.Component,
      observableAttributes: tagDetails.observableAttributes,
      tagName,
      ApplicationWrapper,
    });
  });
}

export type HTMLTagName = string;
export type HTMLTagProperties = {
  Component: ExposableComponentType;
  observableAttributes: string[];
};
export type ComponentListStructure = Record<HTMLTagName, HTMLTagProperties>;
